/**
 * Slice 01 of wps-preview-pixel-parity.
 *
 * Client-side mirror of the server's `wpsXlsxTheme` token table.
 * The two surfaces (React-PDF on the server, HTML/Tailwind on the
 * client) share the same XLSX-faithful WPS layout, and every visual
 * constant the PDF cares about lives in this module under the same
 * key names as `server/src/pdf/wpsXlsxTheme.ts`.
 *
 * Why two files instead of a shared package or shared JSON: the
 * monorepo's TS configs lock each package to its own `src/` tree
 * (CRA enforces it on the client; the server `tsconfig` has
 * `rootDir: src`). A shared file would require introducing a build
 * step or a workspace package just for ~14 numeric constants —
 * overhead the PRD explicitly rejects. Instead, both files export
 * the same `cssVars` map under the same keys and a parallel test
 * pins the values on each side. Drift is caught the moment a
 * contributor edits one without the other.
 *
 * The values here are the source of truth for the HTML canvas.
 * When changing a token, change BOTH `wpsXlsxTheme.ts` (server)
 * and this file together, and update both theme tests.
 */

export const cssVars = {
  '--wps-brand-red': '#EB1C37',
  '--wps-brand-red-tint': '#FDECEE',
  '--wps-hairline-width': '0.5pt',
  '--wps-hairline-color': '#000000',
  '--wps-cell-pad-x': '2pt',
  '--wps-cell-pad-y': '1pt',
  '--wps-page-margin': '16pt',
  '--wps-page-width': '1191pt',
  '--wps-paper-edge': '#E5E7EB',
  '--wps-hover-bg': 'rgba(0, 0, 0, 0.04)',
  '--wps-font-family': '"Helvetica Neue", Helvetica, Arial, sans-serif',
  '--wps-font-size-title': '14pt',
  '--wps-font-size-section-header': '11pt',
  '--wps-font-size-body': '10pt',
  '--wps-font-size-caption': '9pt',
  '--wps-font-size-micro': '8pt'
} as const

export type WpsPreviewCssVarKey = keyof typeof cssVars
